import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import { CONFIGURATION_SECTION_OPTIONS_QUERY } from "../queries";
import type { CoverageRoleKind, CoverageSection, CoverageStaff } from "../types";
import StaffUserAutocomplete from "./StaffUserAutocomplete";

type AssignCoverageRoleDialogProps = {
  open: boolean;
  onClose: () => void;
  onAssign: (input: { sectionId: string; backofficeUserId: string }) => void;
  saving?: boolean;
  headline: string;
  staffLabel: string;
  kind: CoverageRoleKind;
  sections: CoverageSection[];
  sectionId?: string;
  sectionName?: string;
  currentPerson?: CoverageStaff | null;
  person?: CoverageStaff | null;
};

function personForKind(kind: CoverageRoleKind, section: CoverageSection) {
  return kind === "recruiter" ? section.recruiter : section.playerEngagementConsultant;
}

export default function AssignCoverageRoleDialog({
  open,
  onClose,
  onAssign,
  saving = false,
  headline,
  staffLabel,
  kind,
  sections,
  sectionId: lockedSectionId,
  sectionName: lockedSectionName,
  currentPerson,
  person: lockedPerson
}: AssignCoverageRoleDialogProps) {
  const [sectionId, setSectionId] = useState(lockedSectionId ?? "");
  const [person, setPerson] = useState<CoverageStaff | null>(lockedPerson ?? null);
  const sectionLocked = Boolean(lockedSectionId);
  const personLocked = Boolean(lockedPerson);

  const { data: sectionsData } = useQuery(CONFIGURATION_SECTION_OPTIONS_QUERY, {
    variables: { first: 100 },
    skip: !open
  });

  useEffect(() => {
    if (open) {
      setSectionId(lockedSectionId ?? "");
      setPerson(lockedPerson ?? null);
    }
  }, [open, lockedSectionId, lockedPerson]);

  const sectionOptions = useMemo(() => {
    const byId = new Map<string, { value: string; label: string }>();

    for (const section of sections) {
      byId.set(section.id, { value: section.id, label: section.name });
    }

    for (const section of sectionsData?.crmSections.nodes ?? []) {
      byId.set(section.id, { value: section.id, label: section.name });
    }

    if (lockedSectionId && lockedSectionName && !byId.has(lockedSectionId)) {
      byId.set(lockedSectionId, { value: lockedSectionId, label: lockedSectionName });
    }

    return Array.from(byId.values());
  }, [lockedSectionId, lockedSectionName, sections, sectionsData]);

  const incumbentName = useMemo(() => {
    if (!personLocked && currentPerson?.name) {
      return currentPerson.name;
    }

    const fromTable = sections.find((section) => section.id === sectionId);
    const fromOptions = (sectionsData?.crmSections.nodes ?? []).find((section) => section.id === sectionId);
    const assigned = fromTable ?? fromOptions;
    if (!assigned) {
      return undefined;
    }

    const incumbent = personForKind(kind, assigned);
    if (personLocked && incumbent?.id === lockedPerson?.id) {
      return undefined;
    }

    return incumbent?.name ?? undefined;
  }, [currentPerson, kind, lockedPerson?.id, personLocked, sectionId, sections, sectionsData]);

  const canSave = Boolean(sectionId && person && !saving);

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline={headline}
      className="w-[560px] max-w-[95vw] max-h-[85vh]"
      hasFormElements
      disableClose={saving}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={() => person && onAssign({ sectionId, backofficeUserId: person.id })}
            disabled={!canSave}
            loading={saving}
            testId="assign-coverage-role-save"
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Select
          label="Section"
          searchable
          required
          disabled={sectionLocked}
          value={sectionId}
          options={sectionOptions}
          onSelect={setSectionId}
          supportingText={incumbentName ? `This replaces ${incumbentName} for this section.` : undefined}
          data-testid="assign-coverage-role-section"
        />
        <StaffUserAutocomplete
          label={staffLabel}
          required
          disabled={personLocked}
          selectedUser={person}
          onSelect={setPerson}
        />
      </div>
    </Dialog>
  );
}
